import type { CSSProperties } from "react"
import type { GlyphRow } from "@/utils/glyphMotion"
import styles from "@/styles/components/AsciiAssembly.module.css"

/**
 * An ASCII drawing whose characters are placed one span at a time, so each can
 * be flown in independently. The sibling of `AsciiArt`, which sets the same
 * motifs as a static `<pre>` — use that one anywhere nothing is moving.
 *
 * Every row is a fixed-track grid rather than a line of text, because a glyph
 * being translated and rotated cannot also be holding a column open. The cell
 * width is what keeps the drawing lined up while its characters are still
 * scattered across the screen; a `<pre>` would collapse the moment one moved.
 *
 * Decorative in every place it appears — the screen around it already says what
 * is happening — so the whole thing is `aria-hidden`.
 */

interface AsciiAssemblyProps {
  /** Already-computed glyph styles. See `utils/glyphMotion`. */
  rows: GlyphRow[]
  /** How many characters wide the drawing is, and how wide one cell sits. */
  columns: number
  cell: string
  fontSize: string
  lineHeight: string
  className?: string
}

export default function AsciiAssembly({
  rows,
  columns,
  cell,
  fontSize,
  lineHeight,
  className,
}: AsciiAssemblyProps) {
  const metrics = {
    "--assembly-columns": columns,
    "--assembly-cell": cell,
    "--assembly-size": fontSize,
    "--assembly-line": lineHeight,
  } as CSSProperties

  return (
    <div
      className={[styles.assembly, className].filter(Boolean).join(" ")}
      style={metrics}
      aria-hidden="true"
    >
      {rows.map((row, index) => (
        <div className={styles.row} key={index}>
          {/* A motif may have a blank line in it — the deck's two hands are
              separated by one. An empty grid row collapses to nothing, which
              would join the two halves of the drawing, so it is held open by a
              space. */}
          {row.chars.length === 0 ? <span className={styles.glyph}>{" "}</span> : null}
          {row.chars.map((glyph, position) => (
            <span
              key={position}
              className={styles.glyph}
              style={{
                opacity: glyph.opacity,
                transform: glyph.transform,
                color: glyph.color,
              }}
            >
              {glyph.ch}
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}
